import { contextStore } from "../auth/context-store.js";
import { DEFAULT_DARK_THEME, FONT_OPTIONS, defaultVisualDesign, deriveThemeTokens, effectivePalette, isLightPalette } from "../../domain/studio/visual-design.js";
import type { AppAppearance, LoginAppearance } from "../../application/services/studio-design-service.js";

/**
 * STUDIO INSPECT IA — aplica a identidade PUBLICADA na aplicação, sem deploy.
 *
 * Só sobrescreve o que DIFERE do padrão: com a versão V0 (nenhuma
 * publicação), nenhuma propriedade é tocada e o `styles.css` vale como
 * sempre. Publicar chama `applyAppearance` de novo — a troca é imediata.
 */

/**
 * Derivação da paleta de FÁBRICA: é contra ela que se decide o que gravar.
 * Com o tema Original nenhuma variável é tocada e o styles.css vale como sempre.
 */
const BASELINE_TOKENS = deriveThemeTokens(DEFAULT_DARK_THEME);

let current: AppAppearance | null = null;
let colorSchemeListener: ((e: MediaQueryListEvent) => void) | null = null;

function setOrClear(root: HTMLElement, prop: string, value: string | null) {
  if (value === null) root.style.removeProperty(prop);
  else root.style.setProperty(prop, value);
}

function setIcon(rel: string, url: string | null) {
  if (typeof document === "undefined") return;
  let link = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"][data-studio]`);
  if (!url) { link?.remove(); return; }
  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    link.dataset["studio"] = "1";
    document.head.appendChild(link);
  }
  link.href = url;
}

function stackOf(fontId: string): string {
  return FONT_OPTIONS.find(f => f.id === fontId)?.stack ?? FONT_OPTIONS[0]!.stack;
}

export function applyAppearance(appearance: AppAppearance | null): void {
  if (typeof document === "undefined") return;
  current = appearance;
  const root = document.documentElement;
  const d = defaultVisualDesign();
  const theme = appearance?.theme ?? d.theme;
  const prefersDark = typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches;
  const palette = effectivePalette(theme, prefersDark);
  // TODAS as variáveis do tema — não só parte delas (ver deriveThemeTokens).
  const tokens = deriveThemeTokens(palette);
  for (const [name, value] of Object.entries(tokens)) setOrClear(root, name, value !== BASELINE_TOKENS[name] ? value : null);
  root.dataset["theme"] = theme.mode === "ESCURO" ? "dark" : theme.mode === "CLARO" ? "light" : "auto";
  // Superfície efetiva (inclusive no AUTOMÁTICO): liga a camada clara do styles.css.
  root.dataset["surface"] = isLightPalette(palette) ? "light" : "dark";
  root.dataset["themeId"] = theme.themeId;
  if (colorSchemeListener && typeof window.matchMedia === "function") window.matchMedia("(prefers-color-scheme: dark)").removeEventListener("change", colorSchemeListener);
  colorSchemeListener = null;
  if (theme.mode === "AUTOMATICO" && typeof window.matchMedia === "function") {
    colorSchemeListener = () => applyAppearance(current);
    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", colorSchemeListener);
  }

  // Tipografia + densidade: a base é 14px e as telas usam rem.
  const t = appearance?.typography ?? d.typography;
  const l = appearance?.layout ?? d.layout;
  setOrClear(root, "--font-sans", t.bodyFont !== d.typography.bodyFont ? stackOf(t.bodyFont) : null);
  setOrClear(root, "--font-heading", t.headingFont !== t.bodyFont ? stackOf(t.headingFont) : null);
  setOrClear(root, "--font-weight-base", t.baseWeight !== 400 ? String(t.baseWeight) : null);
  const size = 14 + (t.scale === "COMPACTA" ? -1 : t.scale === "AMPLA" ? 1 : 0) + (l.density === "COMPACTO" ? -1 : l.density === "CONFORTAVEL" ? 1 : 0);
  setOrClear(root, "--font-size-root", size !== 14 ? `${size}px` : null);
  if (t.letterSpacing === "AMPLO") root.dataset["letterSpacing"] = "AMPLO"; else delete root.dataset["letterSpacing"];

  // Layout: largura do menu, raio, estilo de card, espaçamento.
  setOrClear(root, "--sidebar-width", l.sidebarWidth !== d.layout.sidebarWidth ? `${l.sidebarWidth}px` : null);
  const radiusChanged = l.radius !== d.layout.radius;
  setOrClear(root, "--radius-sm", radiusChanged ? `${Math.max(0, l.radius - 2)}px` : null);
  setOrClear(root, "--radius-md", radiusChanged ? `${l.radius}px` : null);
  setOrClear(root, "--radius-lg", radiusChanged ? `${l.radius + 2}px` : null);
  setOrClear(root, "--radius-xl", radiusChanged ? `${l.radius + 4}px` : null);
  if (l.cardStyle !== d.layout.cardStyle) root.dataset["cardStyle"] = l.cardStyle; else delete root.dataset["cardStyle"];
  if (l.spacing !== d.layout.spacing) root.dataset["spacing"] = l.spacing; else delete root.dataset["spacing"];

  // Painel de Engenharia: só as cores (os dados continuam do domínio operacional).
  const eng = appearance?.dashboard.engineering ?? d.dashboard.engineering;
  const engDefault = d.dashboard.engineering;
  setOrClear(root, "--studio-eng-accent", eng.accent !== engDefault.accent ? eng.accent : null);
  setOrClear(root, "--studio-eng-accent-sand", eng.accentSand !== engDefault.accentSand ? eng.accentSand : null);
  setOrClear(root, "--studio-eng-accent-done", eng.accentDone !== engDefault.accentDone ? eng.accentDone : null);
  setOrClear(root, "--studio-eng-accent-warn", eng.accentWarn !== engDefault.accentWarn ? eng.accentWarn : null);
  setOrClear(root, "--studio-eng-accent-info", eng.accentInfo !== engDefault.accentInfo ? eng.accentInfo : null);

  setIcon("icon", appearance?.logos.favicon ?? null);
  setIcon("apple-touch-icon", appearance?.logos.appIcon ?? null);
}

/** Depois do login: a aparência completa (só para sessão autenticada). */
export async function loadAndApplyAppearance(): Promise<AppAppearance | null> {
  try {
    const c = contextStore.get();
    const ctx = { activeClientId: c.activeClientId || undefined, permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null };
    const service = window.app?.platform?.studioDesignService;
    if (!service) return null;
    const appearance = await service.getAppearance(ctx as never);
    applyAppearance(appearance);
    return appearance;
  } catch {
    return null;
  }
}

/** Antes do login: só o que é público (favicon, ícone). */
export async function loadPublicAppearance(): Promise<LoginAppearance | null> {
  try {
    const service = window.app?.platform?.studioDesignService;
    if (!service) return null;
    const pub = await service.getPublicLoginAppearance();
    setIcon("icon", pub.faviconUrl);
    setIcon("apple-touch-icon", pub.appIconUrl);
    return pub;
  } catch {
    return null;
  }
}
